/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { capitalize } from 'lodash'
import React from 'react'
import { FormattedDate } from 'react-intl'

import { EuiCallOut } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { BillingSubscriptionLevel } from '@modules/ui-types'
import { getSubscriptionChangeEventType } from '@modules/billing-lib/utils'

import { numericDateTime } from '@/config/dates'

interface Props {
  currentLevel: BillingSubscriptionLevel
  pendingLevel?: BillingSubscriptionLevel
  pendingLevelValidFrom?: string
}

const PendingLevelNotice = ({ currentLevel, pendingLevel, pendingLevelValidFrom }: Props) => {
  if (!pendingLevel || !pendingLevelValidFrom) {
    return null
  }

  return (
    <EuiCallOut
      size='s'
      title={
        <FormattedMessage
          id='subscription-section.pending-subscription-update-notice.message'
          defaultMessage='Subscription is currently pending an {changeType} to {pendingLevel} This change becomes effective on {pendingLevelValidFrom}.'
          values={{
            pendingLevel: capitalize(pendingLevel),
            pendingLevelValidFrom: (
              <FormattedDate value={pendingLevelValidFrom} {...numericDateTime} />
            ),
            changeType: {
              upgrade: (
                <FormattedMessage
                  id='subscription-change.event-type.upgrade'
                  defaultMessage='upgrade'
                />
              ),
              downgrade: (
                <FormattedMessage
                  id='subscription-change.event-type.downgrade'
                  defaultMessage='downgrade'
                />
              ),
            }[getSubscriptionChangeEventType(currentLevel, pendingLevel)],
          }}
        />
      }
      iconType='clock'
      data-test-id='pending-subscription-update-notice'
    />
  )
}

export default PendingLevelNotice
